import { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { BACKLOG_TOP_DEFAULT, type GroomingStage } from "@ttc-spectree/toolbox-contract";
import {
  BookOpen,
  Bug,
  Check,
  ChevronRight,
  Eye,
  Kanban,
  Keyboard,
  List,
  MessageCircleQuestion,
  PanelRightOpen,
  PenLine,
  Plus,
  Search,
  Send,
  X,
  type LucideIcon,
} from "lucide-react";
import { AdoReadErrorCard } from "@/components/grooming/ado-read-error-card";
import {
  GroomingProgressPanel,
  ProgressPanelButton,
  type ProgressItemsKind,
} from "@/components/grooming/grooming-progress-panel";
import { GroomingProgressItems } from "@/components/grooming/grooming-progress-items";
import { buildGroomingSummary } from "@/lib/grooming/grooming-summary";
import { Chip, Segmented, SizeChip } from "@/components/grooming/grooming-chips";
import {
  AUTHOR_QUESTIONS_FOCUS,
  ReadingPane,
  stageOfItem,
  useAgendaMembership,
  type AgendaMembership,
} from "@/components/grooming/reading-pane";
import { PreparingMarker } from "@/components/grooming/preparing-marker";
import { useBacklogShortcuts } from "@/components/grooming/use-backlog-shortcuts";
import { useRegisterCommands, type GroomingCommand } from "@/components/grooming/use-command-registry";
import { useGroomingBacklog, useMetricsAfterBacklogRead } from "@/hooks/queries/use-grooming";
import { useMe } from "@/hooks/queries/use-me";
import { useCan, useCanAnywhere } from "@/hooks/queries/use-toolbox-access";
import { useWorkItem } from "@/hooks/queries/use-work-items";
import { usePageTitle } from "@/hooks/use-page-title";
import type {
  GroomingBacklog,
  GroomingBacklogItem,
  GroomingBacklogSort,
  GroomingBacklogTop,
  GroomingMetrics,
} from "@/lib/api/grooming";
import type { WorkItemDetail } from "@/lib/api/work-items";
import {
  PROGRESS_WINDOW_DEFAULT,
  PROGRESS_ITEMS_PARAM,
  progressItemsKindOf,
  readProgressPanelOpen,
  writeProgressPanelOpen,
  type ProgressWindow,
} from "@/lib/grooming/grooming-progress";
import { formulatorImprovePath } from "@/lib/api/work-item-interview";
import {
  BACKLOG_SORT_LABELS,
  BACKLOG_SORT_MENU,
  BACKLOG_SORT_SCOPE_WORDS,
  BACKLOG_SORT_WORDS,
  createdFull,
  createdShort,
  useBacklogSort,
  whoDate,
} from "@/lib/grooming/backlog-sort";
import {
  BRIEF_STATE_WORDS,
  briefStateOf,
  lowerFirst,
  runwaySentence,
  STAGE_LABELS,
  STAGE_TONES,
  type BriefState,
  type NextStep,
} from "@/lib/grooming/plain-words";
import {
  askedStateWords,
  askerLine,
  authorsAskedNote,
  briefsNotPostedNote,
  needsInfoDetail,
  nextStepView,
  type NextStepCapabilities,
} from "@/lib/grooming/next-step";
import { groomingPath } from "@/lib/grooming/paths";
import { recentItemState, recordRecentItem, updateRecentItem } from "@/lib/grooming/recent-items";
import {
  clampPaneWidth,
  defaultPaneWidth,
  LIST_MIN_WIDTH,
  PANE_WIDTH_MIN,
  readPaneWidth,
  writePaneWidth,
} from "@/lib/grooming/pane-width";
import { cn } from "@/lib/utils";
import { TOOLBOX_PAGE_TITLE, TOOLBOX_TEXT } from "@/lib/toolbox/type-scale";

/**
 * Backlog, the Grooming landing page (epic PI-SPEC-SW-E279 task
 * PI-SPEC-SW-1390-2; reworked by epic PI-SPEC-SW-E280 feature
 * PI-SPEC-SW-1401 from Claude Design "SpecTree Grooming Desktop").
 *
 * Three tiles (groomed points with the runway, ready to groom, needs
 * information), the exception line only when current-sprint items skipped
 * grooming, and the backlog as a List or as three Stages columns, for the
 * top 30 or the whole backlog. The sort menu (E280, PI-SPEC-SW-1406) orders
 * it Newest first (default), Oldest first or in ADO order, server side
 * before the top-30 cut, and is remembered per person (localStorage); the
 * list's Created column shows each item's ADO created date. Opening an item no longer
 * changes page: its summary opens in the reading pane beside the list (or
 * over the board), and `/grooming/:project/items/:id` deep-links to it.
 *
 * Keys: J/K (or ↓/↑) move, A adds a ready-to-groom item to the shared
 * session agenda (with `manage-agenda`, E283), B switches List / Stages, Esc
 * closes the pane, / finds; they are listed behind the header's keyboard
 * button (E289, UX-PM-30), so the subtitle stays one line. The
 * find box filters the whole backlog (a `top=all` read) by item ID or title
 * words, client-side (E280 decision 7). Every title comes from the tracker:
 * rendered as text only.
 */

type View = "list" | "stages";

const STAGE_ORDER: readonly GroomingStage[] = ["needs-information", "ready-to-groom", "groomed"];

/** A row's state line: the stage, the asked state below the bar, the brief's state when a brief exists, and the session. */
export interface RowStateLine {
  stage: string;
  /** E292 (frame D1): "author asked 6 days ago" or "not asked yet" for a below-bar item; null otherwise. */
  asked: { asked: boolean; words: string } | null;
  /** The brief's state words ("brief not posted", "brief posted", …), or null without a brief. */
  brief: { state: BriefState; words: string } | null;
  inSession: boolean;
  /** The whole line as text, for the title attribute and tests. */
  text: string;
}

/**
 * The state line under the title (UX-PM-09, frames 1a and D1): the stage,
 * for a below-bar item whether its author was asked ("Needs information ·
 * author asked 6 days ago", "· not asked yet"), then the brief's state in the
 * shared words of plain-words.ts whenever a brief exists ("Ready to groom ·
 * brief not posted", "Groomed · brief posted"), then "in session". Pure;
 * exported for tests.
 */
export function rowStateLine(
  item: Pick<GroomingBacklogItem, "stage" | "aiSuggestion" | "authorAsk">,
  inSession: boolean,
  now: Date = new Date()
): RowStateLine {
  const stage: string = STAGE_LABELS[item.stage];
  const asked = askedStateWords(item, now);
  const suggestion = item.aiSuggestion;
  const brief = suggestion
    ? (() => {
        const state = briefStateOf({
          commentStatus: suggestion.commentStatus,
          stale: suggestion.stale,
        });
        return { state, words: lowerFirst(BRIEF_STATE_WORDS[state]) };
      })()
    : null;
  const parts = [stage, ...(asked ? [asked.words] : []), ...(brief ? [brief.words] : []), ...(inSession ? ["in session"] : [])];
  return { stage, asked, brief, inSession, text: parts.join(" · ") };
}

/** The state line's text; the brief's state is blue until the brief is posted (frame 1a). */
function RowState({ line }: { line: RowStateLine }) {
  return (
    <span
      className="truncate text-xs text-muted-foreground"
      title={line.text}
      data-testid="row-state"
    >
      {line.stage}
      {line.asked && (
        <>
          {" · "}
          <span className={cn(line.asked.asked && "text-[#D4D4D8]")} data-testid="row-asked">
            {line.asked.words}
          </span>
        </>
      )}
      {line.brief && (
        <>
          {" · "}
          <span
            className={cn(line.brief.state === "not-posted" && "text-[#6CC4FF]")}
            data-brief-state={line.brief.state}
          >
            {line.brief.words}
          </span>
        </>
      )}
      {line.inSession ? " · in session" : ""}
    </span>
  );
}

/**
 * A backlog row built from the item read, for a linked item the whole-backlog
 * read does not hold (already in a sprint, closed, or the read failed): the
 * stage by the backlog's rule, the brief's size and state from the item's
 * brief block, no created date or position, and no "Being prepared by"
 * (only the backlog read knows it). Pure; exported for tests.
 */
export function rowFromItem(item: WorkItemDetail): GroomingBacklogItem {
  const stage = stageOfItem(item);
  const block = item.groomingBrief ?? null;
  const brief = block?.brief ?? null;
  const estimate = typeof item.estimate === "number" && item.estimate > 0 ? item.estimate : null;
  const teamPoints = block?.teamPoints ?? estimate;
  const stale = block?.stale ?? false;
  const grooming = item.readiness.grooming;
  return {
    itemId: item.ref.id,
    ref: item.ref,
    title: item.title,
    kind: item.kind,
    typeName: item.typeName,
    state: item.state.name,
    iteration: item.classification.iteration,
    backlogRank: null,
    createdDate: null,
    priority: item.priority ?? null,
    tags: item.tags,
    stage,
    readiness: {
      tier: grooming.tier,
      missing: grooming.missing,
      ...(grooming.criteria ? { criteria: grooming.criteria } : {}),
    },
    teamPoints,
    aiSuggestion: brief
      ? {
          source: "ai",
          points: brief.suggestedPoints,
          low: brief.pointsLow,
          high: brief.pointsHigh,
          confidence: brief.confidence,
          briefId: brief.id,
          stale,
          commentStatus: brief.commentStatus,
          createdAt: brief.createdAt,
        }
      : null,
    size:
      teamPoints !== null
        ? { source: "team", points: teamPoints }
        : brief
          ? { source: "ai", points: brief.suggestedPoints, low: brief.pointsLow, high: brief.pointsHigh }
          : null,
    hasCurrentBrief: brief !== null && !stale,
    nextAction:
      stage === "needs-information" ? "ask-author" : stage === "groomed" ? "ready" : brief ? "review-brief" : "add-to-session",
    preparing: null,
    adoPosition: null,
  };
}

/**
 * Each row's # (E292, UX-PM-12): its place in the ADO backlog (`adoPosition`)
 * in every sort, filter and find. A row without one (an older SpecTree) keeps
 * its place in the list only when the list is in ADO order, since a place in
 * a date order is no backlog claim. Never ADO's raw backlogRank. Pure;
 * exported for tests.
 */
export function backlogPositions(
  rows: readonly Pick<GroomingBacklogItem, "itemId" | "adoPosition">[],
  rankOrdered: boolean
): Map<string, number> {
  const positions = new Map<string, number>();
  rows.forEach((row, index) => {
    const place = typeof row.adoPosition === "number" ? row.adoPosition : rankOrdered ? index + 1 : null;
    if (place !== null) positions.set(row.itemId, place);
  });
  return positions;
}

/**
 * True when an item matches the find box: its ID (with or without `AB#` or
 * `#`) contains the digits typed, or its title contains every word typed.
 * Case-insensitive. Pure; exported for tests.
 */
export function matchesFind(item: Pick<GroomingBacklogItem, "itemId" | "title">, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const id = q.replace(/^ab#/, "").replace(/^#/, "");
  if (/^\d+$/.test(id) && item.itemId.includes(id)) return true;
  const title = item.title.toLowerCase();
  return q.split(/\s+/).every((word) => title.includes(word));
}

/** A stage tile: a filter button (E292, UX-PM-11, frame D1). A second click shows everything again. */
function Tile({
  eyebrow,
  value,
  unit,
  note,
  testId,
  active,
  onToggle,
}: {
  eyebrow: string;
  value: number;
  unit: string;
  note: string;
  testId: string;
  active: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      className={cn(
        "glass-card flex flex-col gap-1 rounded-[10px] border px-3.5 py-[11px] text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50",
        active ? "border-[#F59E0B] bg-[#15120B]" : "hover:border-zinc-600"
      )}
      data-testid={testId}
      data-active={active ? "true" : undefined}
      aria-pressed={active}
      onClick={onToggle}
    >
      <span className="flex items-start gap-2">
        <span
          className={cn(
            "flex-1 text-[11px] font-semibold uppercase tracking-[0.08em]",
            active ? "text-[#FCD34D]" : "text-muted-foreground"
          )}
        >
          {eyebrow}
        </span>
        {active ? (
          <span className="inline-flex items-center gap-1 text-[11.5px] text-[#FBBF24]" data-testid="tile-showing">
            <Check className="h-3 w-3" aria-hidden="true" />
            Showing
          </span>
        ) : (
          <span className="inline-flex items-center text-[11.5px] text-muted-foreground" data-testid="tile-show">
            Show
            <ChevronRight className="h-3 w-3" aria-hidden="true" />
          </span>
        )}
      </span>
      <span className="font-display text-[26px] font-bold leading-none tracking-[-0.02em]">
        {value} <span className="text-[13px] font-medium text-muted-foreground">{unit}</span>
      </span>
      <span className="text-[13px] text-muted-foreground" data-testid={`${testId}-note`}>
        {note}
      </span>
    </button>
  );
}

function itemsWord(count: number): string {
  return `${String(count)} ${count === 1 ? "item" : "items"}`;
}

/**
 * The header's one-line subtitle (frame D1): the order in words, then "top 30
 * of 42" for a top-N read of a longer backlog, else the number of items shown
 * ("9 items"; the matches or filtered rows while finding or filtering). Pure;
 * exported for tests.
 */
export function backlogSubtitle(
  sort: GroomingBacklogSort,
  backlog: Pick<GroomingBacklog, "top" | "items" | "totalBacklogItems"> | undefined,
  shown: number,
  narrowed: boolean
): string {
  const words = BACKLOG_SORT_WORDS[sort];
  if (!backlog) return words;
  if (narrowed) return `${words} · ${itemsWord(shown)}`;
  const loaded = backlog.items.length;
  if (backlog.top === "all" || loaded >= backlog.totalBacklogItems) return `${words} · ${itemsWord(loaded)}`;
  return `${words} · top ${String(loaded)} of ${String(backlog.totalBacklogItems)}`;
}

/** The tiles' scope words: "of all N", or "of the newest / oldest / top N" by sort. Pure; exported for tests. */
export function tileScope(top: GroomingBacklogTop, sort: GroomingBacklogSort, shown: number): string {
  return top === "all" ? `of all ${String(shown)}` : `of the ${BACKLOG_SORT_SCOPE_WORDS[sort]} ${String(shown)}`;
}

function Tiles({
  backlog,
  sort,
  filter,
  onFilter,
}: {
  backlog: GroomingBacklog;
  sort: GroomingBacklogSort;
  filter: GroomingStage | null;
  onFilter: (stage: GroomingStage | null) => void;
}) {
  const scope = tileScope(backlog.top, sort, backlog.items.length);
  const ready = backlog.stageCounts["ready-to-groom"];
  const needsInfo = backlog.stageCounts["needs-information"];
  const toggle = (stage: GroomingStage) => () => {
    onFilter(filter === stage ? null : stage);
  };
  const readyNote = ready > 0 ? (briefsNotPostedNote(backlog.items) ?? "Can go on the next session's agenda") : "Nothing is ready for a session yet";
  const needsInfoNote = needsInfo > 0 ? (authorsAskedNote(backlog.items) ?? "Waiting on their authors") : "Every item has what the team needs";
  return (
    <div className="grid gap-2.5 md:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)_minmax(0,1fr)]">
      <Tile
        testId="tile-groomed"
        eyebrow="Groomed, ready for a sprint"
        value={backlog.runway.groomedPoints}
        unit={backlog.runway.groomedPoints === 1 ? "point" : "points"}
        note={runwaySentence(backlog.runway.sprints)}
        active={filter === "groomed"}
        onToggle={toggle("groomed")}
      />
      <Tile
        testId="tile-ready"
        eyebrow="Ready to groom"
        value={ready}
        unit={scope}
        note={readyNote}
        active={filter === "ready-to-groom"}
        onToggle={toggle("ready-to-groom")}
      />
      <Tile
        testId="tile-needs-information"
        eyebrow="Needs information"
        value={needsInfo}
        unit={scope}
        note={needsInfoNote}
        active={filter === "needs-information"}
        onToggle={toggle("needs-information")}
      />
    </div>
  );
}

/** What each stage filter shows, under the tiles (frame D2). */
export const STAGE_FILTER_EXPLANATIONS: Record<GroomingStage, string> = {
  "needs-information": "Below the grooming bar. The PM asks the author, or fixes the item in the ADO Formulator.",
  "ready-to-groom": "Meets the grooming bar and has no team size yet. Add it to a session, or review its brief.",
  groomed: "Has the team's size. Ready for a sprint.",
};

/** The stages a `?stage=` value may name. */
const STAGE_PARAM = "stage";

/** The stage filter named by `?stage=`, or null when it is missing or not a stage. Pure; exported for tests. */
export function stageFilterOf(value: string | null): GroomingStage | null {
  return value !== null && (STAGE_ORDER as readonly string[]).includes(value) ? (value as GroomingStage) : null;
}

/** The filter chip row under the tiles (frame D2): the stage and count with ×, what it shows, and Show all. */
function FilterChipRow({ stage, count, onClear }: { stage: GroomingStage; count: number; onClear: () => void }) {
  return (
    <div className="flex flex-wrap items-center gap-2.5 pb-0.5" data-testid="backlog-filter">
      <Chip tone={STAGE_TONES[stage]} className="h-6 gap-1.5 pr-1 text-xs" testId="backlog-filter-chip">
        {STAGE_LABELS[stage]} · {itemsWord(count)}
        <button
          type="button"
          className="inline-flex h-4 w-4 items-center justify-center rounded-full hover:bg-white/10"
          aria-label={`Stop showing only ${STAGE_LABELS[stage]}`}
          onClick={onClear}
        >
          <X className="h-3 w-3" aria-hidden="true" />
        </button>
      </Chip>
      <span className="min-w-0 text-xs text-muted-foreground">{STAGE_FILTER_EXPLANATIONS[stage]}</span>
      <span className="flex-1" />
      <button type="button" className="text-[13px] text-[#6CC4FF] hover:underline" onClick={onClear}>
        Show all
      </button>
    </div>
  );
}

function Exceptions({ backlog, onSelect }: { backlog: GroomingBacklog; onSelect: (itemId: string) => void }) {
  const [open, setOpen] = useState(false);
  const count = backlog.exceptions.length;
  if (count === 0) return null;
  const sprint = backlog.currentIteration?.name ?? "the current sprint";

  return (
    <div className="space-y-2" data-testid="backlog-exceptions">
      <div
        className="flex items-center gap-2.5 rounded-lg border border-amber-500/[0.22] bg-amber-500/[0.07] px-3 py-1.5"
        role="status"
      >
        <Chip tone="warn">Exception</Chip>
        <span className="flex-1 text-sm">
          {count} {count === 1 ? "item" : "items"} went into {sprint} without being groomed.
        </span>
        <button
          type="button"
          className="text-sm text-link hover:underline"
          aria-expanded={open}
          onClick={() => {
            setOpen((value) => !value);
          }}
        >
          {open ? "Hide them" : "Show them"}
        </button>
      </div>
      {open && (
        <ul className="glass-card divide-y divide-[#141417] rounded-[10px]" data-testid="backlog-exception-list">
          {backlog.exceptions.map((item) => (
            <li key={item.itemId} className="flex items-center gap-3 px-4 py-2 text-sm">
              <span className="font-mono text-xs text-muted-foreground">{item.itemId}</span>
              <button
                type="button"
                className="min-w-0 flex-1 truncate text-left hover:underline"
                title={item.title}
                onClick={() => {
                  onSelect(item.itemId);
                }}
              >
                {item.title}
              </button>
              <span className="text-xs text-muted-foreground">{STAGE_LABELS[item.stage]}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function KindIcon({ item }: { item: GroomingBacklogItem }) {
  return item.kind === "bug" ? (
    <Bug className="h-3.5 w-3.5 text-red-400" aria-label="Bug" />
  ) : (
    <BookOpen className="h-3.5 w-3.5 text-sky-300" aria-label={item.typeName} />
  );
}

/** A ready-to-groom row's session toggle: add it to the shared agenda or take it off. */
function RowSessionAction({ item, agenda }: { item: GroomingBacklogItem; agenda: AgendaMembership }) {
  if (item.stage !== "ready-to-groom") return null;
  const inSession = agenda.has(item.itemId);
  // Without manage-agenda (E283) the row only says whether the item is in the session.
  if (!agenda.canEdit) {
    return inSession ? (
      <Chip tone="ok" testId="row-in-session">
        In session
      </Chip>
    ) : null;
  }
  return (
    <button
      type="button"
      className={cn(
        "inline-flex h-[26px] items-center gap-1.5 rounded-lg border px-2.5 text-xs font-medium disabled:opacity-50",
        inSession
          ? "border-green-500/25 bg-green-500/[0.14] text-green-400"
          : "border-[#27272A] bg-[#1A1A1E] text-foreground hover:bg-[#222227]"
      )}
      data-testid="row-action"
      aria-pressed={inSession}
      disabled={agenda.isPending}
      onClick={(event) => {
        event.stopPropagation();
        agenda.toggle(item.itemId);
      }}
    >
      {!inSession && <Plus className="h-3.5 w-3.5" aria-hidden="true" />}
      {inSession ? "In session" : "Add to session"}
    </button>
  );
}

/**
 * What the Next step column needs from the page (E292, UX-PM-11): the
 * signed-in person's e-mail ("you wrote it"), their capabilities on the
 * project, and what a step's button does.
 */
export interface NextStepContext {
  viewerEmail: string | null;
  capabilities: NextStepCapabilities;
  onStep: (item: GroomingBacklogItem, step: NextStep) => void;
}

/** Each action's icon (frame D1). */
const NEXT_STEP_ICONS: Partial<Record<NextStep, LucideIcon>> = {
  "review-brief": Send,
  "add-to-session": Plus,
  "ask-author": MessageCircleQuestion,
  "check-reply": Eye,
  "fix-yourself": PenLine,
};

/**
 * A row's next step (frames D1 and D2): a small button for a person who can
 * take it, else the same words as plain text; Waiting on the author and Ready
 * for a sprint are always text. Add to session is the shared agenda's toggle.
 */
function NextStepCell({
  item,
  agenda,
  context,
}: {
  item: GroomingBacklogItem;
  agenda: AgendaMembership;
  context: NextStepContext;
}) {
  const view = nextStepView(item, context.viewerEmail, context.capabilities);
  if (view.step === "add-to-session" && (view.actionable || agenda.has(item.itemId))) {
    return (
      <span className="flex min-w-0 items-center" data-testid="row-next-step" data-step={view.step} data-actionable={String(view.actionable)}>
        <RowSessionAction item={item} agenda={agenda} />
      </span>
    );
  }
  const Icon = NEXT_STEP_ICONS[view.step];
  return (
    <span className="flex min-w-0 items-center" data-testid="row-next-step" data-step={view.step} data-actionable={String(view.actionable)}>
      {view.actionable ? (
        <button
          type="button"
          className="inline-flex h-[26px] max-w-full items-center gap-1.5 truncate rounded-lg border border-[#27272A] bg-[#1A1A1E] px-2.5 text-xs font-medium text-foreground hover:bg-[#222227]"
          data-testid="next-step-action"
          onClick={(event) => {
            event.stopPropagation();
            context.onStep(item, view.step);
          }}
        >
          {Icon && <Icon className="h-3.5 w-3.5 flex-none" aria-hidden="true" />}
          {view.words}
        </button>
      ) : (
        <span className={cn("truncate text-xs", TOOLBOX_TEXT.faint)} data-testid="next-step-text">
          {view.words}
        </span>
      )}
    </span>
  );
}

/**
 * The List view's layouts (UX-PM-18, UX-PM-30; E292 frames D1 to D4). With no
 * reading pane the list has the whole width: # · ID · Item (the title and its
 * state line) · Created · Size · Next step (frame D1). With Needs information
 * picked on the tiles it shows the author instead: # · ID · Item · Author ·
 * Next step (frame D2). Beside the "How grooming is going" panel it keeps the
 * actions and drops only Created: # · ID · Item · Size · Next step (PKG-PM-E
 * walk E-W5). With the pane open it is compact: # · ID · Item (the title and
 * the state line under it) · Size. Every title and state line
 * carries its full text on hover (UX-PM-21). Literal class strings so
 * Tailwind generates them.
 */
type ListLayout = "full" | "author" | "progress" | "compact";

const LIST_GRIDS: Record<ListLayout, string> = {
  full: "grid grid-cols-[30px_54px_minmax(0,1fr)_120px_60px_168px] items-center gap-2.5",
  author: "grid grid-cols-[30px_54px_minmax(0,1fr)_200px_156px] items-center gap-2.5",
  progress: "grid grid-cols-[30px_54px_minmax(0,1fr)_60px_168px] items-center gap-2.5",
  compact: "grid grid-cols-[30px_56px_minmax(0,1fr)_46px] items-center gap-2.5",
};

/** The column headings of each layout. */
const LIST_HEADINGS: Record<ListLayout, readonly string[]> = {
  full: ["#", "ID", "Item", "Opened", "Size", "Next step"],
  author: ["#", "ID", "Item", "Author", "Next step"],
  progress: ["#", "ID", "Item", "Size", "Next step"],
  compact: ["#", "ID", "Item", "Size"],
};

/** The selected row: a darker background and the red bar on its left edge (frame 1b). */
const SELECTED_ROW = "bg-[#141417] shadow-[inset_3px_0_0_#E11837] hover:bg-[#141417]";

interface ViewProps {
  /** The project, for each row's run-queue badge (epic PI-SPEC-SW-E284). */
  project: string;
  items: GroomingBacklogItem[];
  positions: Map<string, number>;
  selectedId: string | undefined;
  agenda: AgendaMembership;
  onSelect: (itemId: string) => void;
  /** The item a link opened when the list does not hold it, pinned at the top (UX-PM-18), or null. */
  pinned: GroomingBacklogItem | null;
}

interface ListViewProps extends ViewProps {
  /** Full width, the Author layout (Needs information picked) or compact beside the reading pane. */
  layout: ListLayout;
  nextStep: NextStepContext;
}

/** One row's title button; it carries the full title on hover. */
function RowTitle({
  item,
  onSelect,
}: {
  item: GroomingBacklogItem;
  onSelect: (itemId: string) => void;
}) {
  return (
    <button
      type="button"
      className="truncate text-left text-sm focus:outline-none focus-visible:underline"
      title={item.title}
      onClick={(event) => {
        event.stopPropagation();
        onSelect(item.itemId);
      }}
    >
      {item.title}
    </button>
  );
}

/** A row's one-line description (ER-1656); nothing when the row has none. */
function RowSnippet({ snippet }: { snippet: string | null | undefined }) {
  if (!snippet) return null;
  return (
    <span className="truncate text-xs text-[#A1A1AA]" title={snippet} data-testid="row-snippet">
      {snippet}
    </span>
  );
}

export { whoDate };

/** The Opened cell (frame K4): who opened the item over the date. Text only. */
function OpenedCell({ item }: { item: GroomingBacklogItem }) {
  const name = item.createdBy?.displayName ?? "Author not known";
  const date = createdShort(item.createdDate);
  return (
    <span
      className="flex min-w-0 flex-col gap-0.5"
      title={createdFull(item.createdDate) || undefined}
      data-testid="row-opened"
    >
      <span className="truncate text-xs text-[#FAFAFA]">{name}</span>
      {date && <span className="truncate text-xs text-muted-foreground">{date}</span>}
    </span>
  );
}

/** A ready-to-groom row's hover action in the compact list, over the right end of the row. */
function RowActionOverlay({
  item,
  agenda,
  inSession,
}: {
  item: GroomingBacklogItem;
  agenda: AgendaMembership;
  inSession: boolean;
}) {
  if (item.stage !== "ready-to-groom") return null;
  return (
    <div
      className={cn(
        "absolute inset-y-0 right-0 hidden items-center bg-gradient-to-r from-transparent to-[#15151A] to-[24px] pl-6 pr-3 group-hover:flex group-focus-within:flex",
        inSession && "flex"
      )}
    >
      <RowSessionAction item={item} agenda={agenda} />
    </div>
  );
}

interface RowProps {
  project: string;
  item: GroomingBacklogItem;
  positions: Map<string, number>;
  selected: boolean;
  agenda: AgendaMembership;
  onSelect: (itemId: string) => void;
  layout: ListLayout;
  nextStep: NextStepContext;
  /** True for the row pinned above the list because a link opened it (UX-PM-18). */
  pinned?: boolean;
}

/** The # cell: the item's place in the ADO backlog, never ADO's raw backlogRank (a StackRank value such as 675733211). */
function RowPosition({ item, positions }: { item: GroomingBacklogItem; positions: Map<string, number> }) {
  return (
    <span className={cn("font-display text-xs font-semibold", TOOLBOX_TEXT.faint)} data-testid="row-position">
      {positions.get(item.itemId) ?? ""}
    </span>
  );
}

/** The ID cell: the kind icon and the item id. */
function RowId({ item, withIcon }: { item: GroomingBacklogItem; withIcon: boolean }) {
  return (
    <span className="flex min-w-0 items-center gap-1">
      {withIcon && <KindIcon item={item} />}
      <span className="font-mono text-xs text-muted-foreground">{item.itemId}</span>
    </span>
  );
}

/**
 * A below-bar row's second line in the Author layout (frame D2): amber "Item
 * changed on Oct 18, after you asked", else the rules in place and whether
 * anything changed since the ask.
 */
function NeedsInfoDetail({ item }: { item: GroomingBacklogItem }) {
  const detail = needsInfoDetail(item);
  return (
    <span
      className={cn("truncate text-xs", detail.changed ? "text-[#FBBF24]" : "text-muted-foreground")}
      title={detail.text}
      data-testid="row-detail"
      data-changed={detail.changed ? "true" : undefined}
    >
      {detail.text}
    </span>
  );
}

/** The Author cell (frame D2): who created the item, over who asked them and when, or "Not asked yet". Text only. */
function AuthorCell({ item, viewerEmail }: { item: GroomingBacklogItem; viewerEmail: string | null }) {
  const name = item.createdBy?.displayName ?? "Author not known";
  const asked = askerLine(item, viewerEmail);
  return (
    <span className="flex min-w-0 flex-col gap-0.5" data-testid="row-author">
      <span className="truncate text-sm text-[#FAFAFA]" title={name} data-testid="row-author-name">
        {name}
      </span>
      <span className="truncate text-xs text-muted-foreground" title={asked} data-testid="row-author-asked">
        {asked}
      </span>
    </span>
  );
}

/** One List view row, in the full, author, progress or compact layout. */
function ListRow({ project, item, positions, selected, agenda, onSelect, layout, nextStep, pinned = false }: RowProps) {
  const inSession = agenda.has(item.itemId);
  const line = rowStateLine(item, inSession);
  return (
    <li
      className={cn(
        LIST_GRIDS[layout],
        "group relative cursor-pointer border-b border-[#141417] px-5 transition-colors hover:bg-[#0F0F12]",
        "min-h-[66px] py-2",
        selected && SELECTED_ROW
      )}
      data-testid="backlog-row"
      data-item-id={item.itemId}
      data-stage={item.stage}
      data-pinned={pinned ? "true" : undefined}
      aria-selected={selected}
      onClick={() => {
        onSelect(item.itemId);
      }}
    >
      <div className="self-start pt-0.5">
        <RowPosition item={item} positions={positions} />
      </div>
      <div className="self-start pt-0.5">
        <RowId item={item} withIcon={layout !== "compact"} />
      </div>
      <div className="flex min-w-0 flex-col gap-0.5">
        <RowTitle item={item} onSelect={onSelect} />
        <RowSnippet snippet={item.snippet} />
        {layout === "author" ? (
          <span className="flex min-w-0 text-xs text-muted-foreground" data-testid="row-who-date">
            <span className="min-w-0 shrink truncate" title={whoDate(item)}>{whoDate(item)}</span>
            <span className="shrink-0 whitespace-pre">{" · "}</span>
            <NeedsInfoDetail item={item} />
          </span>
        ) : layout === "full" ? (
          <RowState line={line} />
        ) : (
          <span className="flex min-w-0 text-xs text-muted-foreground" data-testid="row-who-date">
            <span className="min-w-0 shrink truncate" title={whoDate(item)}>{whoDate(item)}</span>
            <span className="shrink-0 whitespace-pre">{" · "}</span>
            <RowState line={line} />
          </span>
        )}
        {/* E283: informational only; the row and its actions stay as they are. E284: plus this computer's queue badge. */}
        <PreparingMarker preparing={item.preparing} project={project} itemId={item.itemId} />
      </div>
      {(layout === "full" || layout === "progress") && (
        <>
          {layout === "full" && <OpenedCell item={item} />}
          <span>
            <SizeChip size={item.size} />
          </span>
          <NextStepCell item={item} agenda={agenda} context={nextStep} />
        </>
      )}
      {layout === "author" && (
        <>
          <AuthorCell item={item} viewerEmail={nextStep.viewerEmail} />
          <NextStepCell item={item} agenda={agenda} context={nextStep} />
        </>
      )}
      {layout === "compact" && (
        <>
          <span>
            <SizeChip size={item.size} />
          </span>
          <RowActionOverlay item={item} agenda={agenda} inSession={inSession} />
        </>
      )}
    </li>
  );
}

/** The eyebrow over a row or card pinned because a link opened it (UX-PM-18). */
const PINNED_LABEL = "Opened from a link";

function ListView({
  project,
  items,
  positions,
  selectedId,
  agenda,
  onSelect,
  layout,
  nextStep,
  pinned,
}: ListViewProps) {
  const rowProps = { project, positions, agenda, onSelect, layout, nextStep };
  return (
    <div className="min-w-0 flex-1" data-testid="backlog-list" data-layout={layout}>
      <div
        className={cn(
          LIST_GRIDS[layout],
          "border-b border-border px-5 py-2 text-[11px] font-semibold uppercase tracking-[0.06em]",
          TOOLBOX_TEXT.faint
        )}
        role="row"
      >
        {LIST_HEADINGS[layout].map((heading) => (
          <span key={heading}>{heading}</span>
        ))}
      </div>
      {pinned && (
        <section aria-label={PINNED_LABEL} className="border-b border-border" data-testid="backlog-pinned">
          <p className={cn("px-5 pb-1 pt-2.5", TOOLBOX_TEXT.eyebrow)}>{PINNED_LABEL}</p>
          <ul>
            <ListRow {...rowProps} item={pinned} selected={pinned.itemId === selectedId} pinned />
          </ul>
        </section>
      )}
      <ul>
        {items.map((item) => (
          <ListRow key={item.itemId} {...rowProps} item={item} selected={item.itemId === selectedId} />
        ))}
      </ul>
    </div>
  );
}

/** One Stages board card. */
function StageCard({
  project,
  item,
  positions,
  selected,
  agenda,
  onSelect,
  pinned = false,
}: Omit<RowProps, "layout" | "nextStep">) {
  return (
    <li
      className={cn(
        "flex cursor-pointer flex-col gap-2 rounded-[9px] border bg-[#111113] px-3 py-2.5 transition-colors hover:border-zinc-600",
        selected ? "border-primary hover:border-primary" : "border-border"
      )}
      data-testid="stage-card"
      data-item-id={item.itemId}
      data-pinned={pinned ? "true" : undefined}
      aria-selected={selected}
      onClick={() => {
        onSelect(item.itemId);
      }}
    >
      <div className="flex gap-2">
        {/* Each card shows its place in the ADO backlog (E292), never ADO's raw backlogRank. */}
        <span className={cn("font-display text-xs font-semibold", TOOLBOX_TEXT.faint)} data-testid="card-position">
          #{positions.get(item.itemId) ?? ""}
        </span>
        <button
          type="button"
          className="line-clamp-2 flex-1 text-left text-sm leading-snug focus:outline-none focus-visible:underline"
          title={item.title}
          data-testid="card-title"
          onClick={(event) => {
            event.stopPropagation();
            onSelect(item.itemId);
          }}
        >
          {item.title}
        </button>
      </div>
      <PreparingMarker preparing={item.preparing} project={project} itemId={item.itemId} />
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <KindIcon item={item} />
        <span className="font-mono">{item.itemId}</span>
        <span className="flex-1" />
        {agenda.has(item.itemId) ? <Chip tone="ok">In session</Chip> : <SizeChip size={item.size} />}
      </div>
    </li>
  );
}

function StagesView({ project, items, positions, selectedId, agenda, onSelect, pinned }: ViewProps) {
  return (
    <div className="flex min-w-0 flex-1 flex-col gap-3.5 px-5 py-3.5" data-testid="backlog-stages">
      {pinned && (
        <section aria-label={PINNED_LABEL} className="flex max-w-[360px] flex-col gap-2" data-testid="backlog-pinned">
          <p className={TOOLBOX_TEXT.eyebrow}>{PINNED_LABEL}</p>
          <ul>
            <StageCard
              project={project}
              item={pinned}
              positions={positions}
              selected={pinned.itemId === selectedId}
              agenda={agenda}
              onSelect={onSelect}
              pinned
            />
          </ul>
        </section>
      )}
      <div className="grid min-w-0 gap-3.5 md:grid-cols-3">
        {STAGE_ORDER.map((stage) => {
          const inStage = items.filter((item) => item.stage === stage);
          return (
            <section key={stage} className="flex min-w-0 flex-col gap-2" data-testid={`stage-column-${stage}`}>
              <h2
                className={cn(
                  "flex items-center gap-2 border-t-[3px] px-0.5 pb-1.5 pt-2 font-display text-sm font-semibold",
                  stage === "needs-information" ? "border-amber-500" : stage === "ready-to-groom" ? "border-link" : "border-green-500"
                )}
              >
                <span className="flex-1">{STAGE_LABELS[stage]}</span>
                <span className="text-muted-foreground">{inStage.length}</span>
              </h2>
              {inStage.length === 0 ? (
                <p className="px-0.5 text-sm text-muted-foreground">Nothing here.</p>
              ) : (
                <ul className="flex flex-col gap-2">
                  {inStage.map((item) => (
                    <StageCard
                      key={item.itemId}
                      project={project}
                      item={item}
                      positions={positions}
                      selected={item.itemId === selectedId}
                      agenda={agenda}
                      onSelect={onSelect}
                    />
                  ))}
                </ul>
              )}
            </section>
          );
        })}
      </div>
    </div>
  );
}

/** The keys the backlog answers to, in the order the keyboard panel lists them. */
export function backlogKeys(canAddToSession: boolean): { keys: string; does: string }[] {
  return [
    { keys: "J / ↓", does: "Next item" },
    { keys: "K / ↑", does: "Previous item" },
    ...(canAddToSession ? [{ keys: "A", does: "Add to the session, or take it off" }] : []),
    { keys: "B", does: "Switch List / Stages" },
    { keys: "/", does: "Find an item" },
    { keys: "Esc", does: "Close the summary" },
  ];
}

/**
 * The header's keyboard button (UX-PM-30, frame 1a): the keys live in a small
 * panel instead of a subtitle that wrapped to three lines. Esc or a click
 * outside closes it; while it is open the backlog's own keys are paused, as
 * for any dialog.
 */
function KeyboardKeysButton({ canAddToSession }: { canAddToSession: boolean }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      setOpen(false);
    };
    const onPointerDown = (event: PointerEvent) => {
      if (event.target instanceof Node && wrapRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("pointerdown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("pointerdown", onPointerDown);
    };
  }, [open]);

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        className="inline-flex h-[30px] items-center rounded-lg px-2.5 text-muted-foreground hover:bg-[#141417] hover:text-foreground"
        aria-label="Keyboard keys"
        title="Keyboard keys"
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => {
          setOpen((value) => !value);
        }}
      >
        <Keyboard className="h-4 w-4" aria-hidden="true" />
      </button>
      {open && (
        <div
          role="dialog"
          aria-label="Keyboard keys"
          className="absolute right-0 top-full z-30 mt-2 w-[280px] rounded-[10px] border border-[#27272A] bg-[#111113] p-3 shadow-[0_8px_20px_rgba(0,0,0,0.4)]"
          data-testid="keyboard-keys"
        >
          <p className={cn("mb-2", TOOLBOX_TEXT.eyebrow)}>Keyboard keys</p>
          <dl className="grid grid-cols-[64px_minmax(0,1fr)] gap-x-3 gap-y-1.5 text-[13px]">
            {backlogKeys(canAddToSession).map((entry) => (
              <div key={entry.keys} className="contents">
                <dt>
                  <kbd className="rounded-[5px] border border-[#27272A] bg-[#0E0E10] px-1.5 text-[11px] text-muted-foreground">
                    {entry.keys}
                  </kbd>
                </dt>
                <dd>{entry.does}</dd>
              </div>
            ))}
          </dl>
        </div>
      )}
    </div>
  );
}

/** The find box: item ID or title words, across the whole backlog. Esc clears it. Off while the backlog cannot be read (B6). */
function FindBox({
  value,
  onChange,
  inputRef,
  disabled = false,
}: {
  value: string;
  onChange: (value: string) => void;
  inputRef: RefObject<HTMLInputElement | null>;
  disabled?: boolean;
}) {
  return (
    <div
      className={cn(
        "flex h-8 w-[260px] items-center gap-2 rounded-lg border border-[#27272A] bg-[#111113] px-2.5 focus-within:ring-2 focus-within:ring-primary/50",
        disabled && "opacity-45"
      )}
    >
      <Search className={cn("h-3.5 w-3.5 flex-none", TOOLBOX_TEXT.faint)} aria-hidden="true" />
      <input
        ref={inputRef}
        type="search"
        aria-label="Find items by ID or title"
        placeholder="Find by ID or title"
        className="min-w-0 flex-1 bg-transparent text-sm placeholder:text-[#8B8B94] focus:outline-none [&::-webkit-search-cancel-button]:hidden"
        disabled={disabled}
        value={value}
        onChange={(event) => {
          onChange(event.target.value);
        }}
        onKeyDown={(event) => {
          if (event.key !== "Escape") return;
          event.preventDefault();
          if (value) onChange("");
          else event.currentTarget.blur();
        }}
      />
      {value && !disabled ? (
        <button
          type="button"
          aria-label="Clear the find box"
          className={cn("hover:text-foreground", TOOLBOX_TEXT.faint)}
          onClick={() => {
            onChange("");
            inputRef.current?.focus();
          }}
        >
          <X className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      ) : (
        <kbd className={cn("rounded-[5px] border border-[#27272A] bg-[#0E0E10] px-1.5 text-[11px]", TOOLBOX_TEXT.faint)}>/</kbd>
      )}
    </div>
  );
}

/**
 * The router state of a selection made on this page: `picked` for a click,
 * Enter or a J/K step, `opened` for a click or Enter only. A selection
 * without it (a deep link, Open brief on the Queue) was opened from a link.
 */
interface BacklogPick {
  picked: true;
  opened: boolean;
  /** Ask the author or Check the reply (E292): the pane opens at the author questions (reading-pane's asksForAuthorQuestions). */
  focus?: typeof AUTHOR_QUESTIONS_FOCUS;
}

/** `/grooming/:project/backlog` and `/grooming/:project/items/:id` (the same page with the item's pane open). */
export function GroomingBacklogPage() {
  const { project = "", id: selectedId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const pick = location.state as BacklogPick | null;
  // UX-PM-18: only an item opened from a link is looked up outside the list and pinned.
  const fromLink = Boolean(selectedId) && pick?.picked !== true;
  usePageTitle(selectedId ? `AB#${selectedId} · Backlog · ${project}` : `Backlog · ${project}`);
  const [view, setView] = useState<View>("list");
  const [top, setTop] = useState<GroomingBacklogTop>(BACKLOG_TOP_DEFAULT);
  // Remembered per person in this browser or app, shared with Session; Newest when nothing valid is stored (ER-1656).
  const [sort, setSort] = useBacklogSort();
  const [find, setFind] = useState("");
  const [paneHidden, setPaneHidden] = useState(false);
  // E293 (UX-PM-01, frames E1 and E4): "How grooming is going" for read-grooming-progress holders. Open or
  // closed is remembered in this browser or app (open when nothing is stored); the 30/60/90 window stays
  // while the reading pane swaps in. `allowed` is false while the access read loads, so the control appears
  // only once the API said yes.
  const progressAllowed = useCan(project, "read-grooming-progress").allowed;
  const [progressOpen, setProgressOpenState] = useState(readProgressPanelOpen);
  const [progressWindow, setProgressWindow] = useState<ProgressWindow>(PROGRESS_WINDOW_DEFAULT);
  const setProgressOpen = useCallback((open: boolean) => {
    setProgressOpenState(open);
    writeProgressPanelOpen(open);
  }, []);
  // E289 (UX-PM-18): the pane's width is remembered on this computer; expanded shows it full width.
  // Null until a width is remembered: the pane then opens at a share of the split.
  const [paneWidth, setPaneWidth] = useState<number | null>(readPaneWidth);
  const [paneExpanded, setPaneExpanded] = useState(false);
  const [splitElement, setSplitElement] = useState<HTMLDivElement | null>(null);
  const [splitWidth, setSplitWidth] = useState(0);
  const findRef = useRef<HTMLInputElement>(null);
  const searching = find.trim().length > 0;
  // E292 (UX-PM-11): a tile filters the list to its stage, kept in ?stage= so Home can link to it.
  const [searchParams, setSearchParams] = useSearchParams();
  const stageFilter = stageFilterOf(searchParams.get(STAGE_PARAM));
  const stageSearch = stageFilter ? `?${STAGE_PARAM}=${stageFilter}` : "";
  const setStageFilter = useCallback(
    (next: GroomingStage | null) => {
      setSearchParams(
        (current) => {
          const params = new URLSearchParams(current);
          if (next) params.set(STAGE_PARAM, next);
          else params.delete(STAGE_PARAM);
          return params;
        },
        // Keep the selection's router state, so a picked item never turns into a link-opened (pinned) one.
        { replace: true, state: location.state as unknown }
      );
    },
    [setSearchParams, location.state]
  );

  const backlog = useGroomingBacklog(project, top, sort);
  // E293: the Backlog read records team points, so How grooming is going waits for it and re-reads after it.
  useMetricsAfterBacklogRead(project, backlog.dataUpdatedAt);
  // A link to an item the loaded list does not hold (UX-PM-18): look for it in the whole backlog.
  // An item picked on this page is in the list it was picked from, so a Top or sort change reads nothing more.
  const outsideLoaded =
    fromLink && backlog.data !== undefined && !backlog.data.items.some((item) => item.itemId === selectedId);
  // The find box searches the whole backlog: one top=all read in the same order, filtered here (E280 decision 7).
  // The same read finds a linked item outside the loaded list, so no new call shape.
  const whole = useGroomingBacklog(searching || outsideLoaded ? project : undefined, "all", sort);
  const agenda = useAgendaMembership(project);
  // B6 (UX-PM-37): a failed read, or a first read paused because the browser is offline, shows the error card,
  // and the find box, sort, Top / All and view controls are off until the backlog can be read.
  const offlinePaused = backlog.fetchStatus === "paused" && backlog.data === undefined;
  const unreadable = backlog.isError || offlinePaused;

  const source = useMemo(
    () => (searching && whole.data ? whole.data.items : backlog.data?.items) ?? [],
    [searching, whole.data, backlog.data]
  );
  // E292 (UX-PM-12): # is the ADO position in every sort, filter and find, taken from the unfiltered rows.
  const listPositions = useMemo(() => backlogPositions(source, sort === "rank"), [source, sort]);
  // The find box and the stage filter narrow the rows client-side; the tiles keep the read's stage counts.
  const visible = useMemo(
    () =>
      source.filter(
        (item) => (!searching || matchesFind(item, find)) && (stageFilter === null || item.stage === stageFilter)
      ),
    [searching, source, find, stageFilter]
  );

  // Pinned (UX-PM-18, prior review 5.1 B): the linked item when the list on screen (Top 30, sort, find) does not show it.
  const missing = fromLink && backlog.data !== undefined && !visible.some((item) => item.itemId === selectedId);
  // The pane reads the item anyway; this shares that query (no second ADO read) for an item outside the backlog.
  const linkedItem = useWorkItem("ado", missing ? project : undefined, missing ? selectedId : undefined);
  const sourceRow = selectedId ? source.find((item) => item.itemId === selectedId) : undefined;
  const wholeRow = selectedId ? whole.data?.items.find((item) => item.itemId === selectedId) : undefined;
  const pinnedRow = useMemo(
    () => (missing ? (wholeRow ?? sourceRow ?? (linkedItem.data ? rowFromItem(linkedItem.data) : null)) : null),
    [missing, wholeRow, sourceRow, linkedItem.data]
  );
  // The pinned item keeps its place in the whole backlog when that read has it.
  const positions = useMemo(() => {
    if (!pinnedRow || listPositions.has(pinnedRow.itemId)) return listPositions;
    const place = whole.data ? backlogPositions(whole.data.items, sort === "rank").get(pinnedRow.itemId) : undefined;
    const pinnedPlace = place ?? (typeof pinnedRow.adoPosition === "number" ? pinnedRow.adoPosition : undefined);
    return pinnedPlace === undefined ? listPositions : new Map(listPositions).set(pinnedRow.itemId, pinnedPlace);
  }, [pinnedRow, listPositions, whole.data, sort]);
  // The pane's "Being prepared by" and the A key also work for a pinned item; an exception row is known too.
  const exceptionRow = selectedId ? backlog.data?.exceptions.find((item) => item.itemId === selectedId) : undefined;
  const selectedRow = sourceRow ?? pinnedRow ?? exceptionRow;

  // E289 (UX-PM-25): an item opened in the pane (a click, Enter or a link; never a J/K step) joins One
  // item's "Recently opened" on this computer, once per open. A later read only updates its state in place.
  const recentTitle = selectedRow?.title;
  const recentState = selectedRow ? recentItemState(selectedRow) : undefined;
  const opened = fromLink || pick?.opened === true;
  const recordedOpen = useRef<string | null>(null);
  useEffect(() => {
    if (!selectedId || recentTitle === undefined) return;
    const entry = { project, itemId: selectedId, title: recentTitle, ...(recentState ? { state: recentState } : {}) };
    if (opened && recordedOpen.current !== location.key) {
      recordedOpen.current = location.key;
      recordRecentItem(entry);
    } else {
      updateRecentItem(entry);
    }
  }, [project, selectedId, recentTitle, recentState, opened, location.key]);

  /** A click or Enter opens an item; a J/K `step` only moves the selection. */
  const select = useCallback(
    (itemId: string, step = false, focusQuestions = false) => {
      setPaneHidden(false);
      // A click on the linked item keeps it pinned where it is.
      if (itemId === selectedId && fromLink && !focusQuestions) return;
      const state: BacklogPick = {
        picked: true,
        opened: !step,
        ...(focusQuestions ? { focus: AUTHOR_QUESTIONS_FOCUS } : {}),
      };
      void navigate({ pathname: groomingPath(project, `items/${itemId}`), search: stageSearch }, { replace: true, state });
    },
    [navigate, project, selectedId, fromLink, stageSearch]
  );

  // E292 (UX-PM-11): the Next step column. Review brief, Check the reply and Ask the author open the item
  // in the pane (Ask the author and Check the reply at its questions); Fix it yourself opens the Formulator.
  const me = useMe();
  const postSingleBrief = useCan(project, "post-single-brief").allowed;
  const formulator = useCanAnywhere("formulator").allowed;
  const onStep = useCallback(
    (row: GroomingBacklogItem, step: NextStep) => {
      if (step === "add-to-session") agenda.toggle(row.itemId);
      else if (step === "fix-yourself") void navigate(formulatorImprovePath(project, row.itemId));
      else select(row.itemId, false, step === "ask-author" || step === "check-reply");
    },
    [agenda, navigate, project, select]
  );
  const nextStep = useMemo(
    (): NextStepContext => ({
      viewerEmail: me.data?.email ?? null,
      capabilities: { manageAgenda: agenda.canEdit, postSingleBrief, formulator },
      onStep,
    }),
    [me.data?.email, agenda.canEdit, postSingleBrief, formulator, onStep]
  );

  const clearSelection = useCallback(() => {
    void navigate({ pathname: groomingPath(project, "backlog"), search: stageSearch }, { replace: true });
  }, [navigate, project, stageSearch]);

  // E293 (frame E2, PI-SPEC-SW-1505): ?progress= opens the items behind a line as a content view in place of
  // the tiles, the exception strip and the list (six columns do not fit the 380 px panel). It is independent of
  // ?stage=, so the link can be shared and the back button returns to the panel. Never with an item open.
  const progressItems =
    progressAllowed && !selectedId ? progressItemsKindOf(searchParams.get(PROGRESS_ITEMS_PARAM)) : null;
  // The panel holds the right-hand space while no item is picked; picking one swaps in the reading pane.
  const showProgress = progressAllowed && progressOpen && !selectedId && !progressItems;
  /** Set or clear ?progress=, keeping ?stage= and the other params. A new history entry, so Back works. */
  const setProgressItems = useCallback(
    (kind: ProgressItemsKind | null) => {
      setSearchParams((current) => {
        const params = new URLSearchParams(current);
        if (kind) params.set(PROGRESS_ITEMS_PARAM, kind);
        else params.delete(PROGRESS_ITEMS_PARAM);
        return params;
      });
    },
    [setSearchParams]
  );
  /** "How it's going": closes the panel while it shows; otherwise leaves the item or the items view and brings the panel back. */
  const toggleProgress = useCallback(() => {
    if (showProgress) {
      setProgressOpen(false);
      return;
    }
    setPaneExpanded(false);
    if (selectedId) clearSelection();
    else if (progressItems) setProgressItems(null);
    setProgressOpen(true);
  }, [showProgress, selectedId, progressItems, clearSelection, setProgressItems, setProgressOpen]);
  const closeProgress = useCallback(() => {
    setProgressOpen(false);
  }, [setProgressOpen]);
  // Frame E3: Copy summary's plain text, with the Backlog read's runway and the signed-in person's full name.
  const runway = backlog.data?.runway;
  const sender = me.data?.name ?? null;
  const summaryFor = useCallback(
    (metrics: GroomingMetrics) =>
      buildGroomingSummary({
        project,
        metrics,
        runway: runway ?? { groomedPoints: 0, sprints: null },
        sender,
      }),
    [project, runway, sender]
  );
  const openProgressItems = useCallback(
    (kind: ProgressItemsKind) => {
      setProgressItems(kind);
    },
    [setProgressItems]
  );
  /** "How grooming is going" in the items view: back to the panel. */
  const backToProgress = useCallback(() => {
    setProgressOpen(true);
    setProgressItems(null);
  }, [setProgressItems, setProgressOpen]);
  /**
   * A row in the items view: the item in the reading pane (drops ?progress, keeps ?stage=). Opened as a
   * link, not a pick: a sized or prepared item may be outside the loaded list, and the deep-link path
   * looks it up in the whole backlog and pins its row.
   */
  const openProgressItem = useCallback(
    (itemId: string) => {
      setPaneHidden(false);
      void navigate({ pathname: groomingPath(project, `items/${itemId}`), search: stageSearch });
    },
    [navigate, project, stageSearch]
  );

  /** Esc and the close button: the list hides its pane; the board clears the selection. */
  const close = useCallback(() => {
    setPaneExpanded(false);
    if (view === "list") setPaneHidden(true);
    else clearSelection();
  }, [view, clearSelection]);

  // The split's width bounds the pane, so the list keeps at least 360 px (the window is at least 1180 px).
  useEffect(() => {
    if (!splitElement) return undefined;
    const measure = () => {
      setSplitWidth(splitElement.clientWidth);
    };
    measure();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(splitElement);
    return () => {
      observer.disconnect();
    };
  }, [splitElement]);

  /** A drag or arrow key on the pane's edge; the width is saved when the drag or key press ends. */
  const resizePane = useCallback(
    (px: number, done: boolean) => {
      const next = clampPaneWidth(px, splitWidth);
      setPaneWidth(next);
      if (done) writePaneWidth(next);
    },
    [splitWidth]
  );

  const togglePaneExpanded = useCallback(() => {
    setPaneExpanded((value) => !value);
  }, []);

  const move = useCallback(
    (step: 1 | -1) => {
      if (visible.length === 0) return;
      const index = selectedId ? visible.findIndex((item) => item.itemId === selectedId) : -1;
      const next = index === -1 ? (step === 1 ? 0 : visible.length - 1) : Math.min(Math.max(index + step, 0), visible.length - 1);
      const target = visible[next];
      if (target && target.itemId !== selectedId) select(target.itemId, true);
    },
    [visible, selectedId, select]
  );

  const toggleView = useCallback(() => {
    setView((current) => (current === "list" ? "stages" : "list"));
  }, []);

  useBacklogShortcuts({
    onNext: () => {
      move(1);
    },
    onPrevious: () => {
      move(-1);
    },
    onToggleSession: () => {
      // A acts only on ready-to-groom items; it does nothing for the other stages.
      if (selectedRow?.stage === "ready-to-groom") agenda.toggle(selectedRow.itemId);
    },
    onToggleView: () => {
      if (!unreadable) toggleView();
    },
    onEscape: () => {
      if (!selectedId) return;
      if (view === "list" && paneHidden) return;
      // Full width first goes back to the list; the next Esc closes the pane.
      if (paneExpanded) setPaneExpanded(false);
      else close();
    },
    onFocusFind: () => {
      if (!unreadable) findRef.current?.focus();
    },
    // The items behind a line replace the list: J/K would step through rows that are not on screen.
  }, progressItems === null);

  // Keep the selected row or card in view as J/K move.
  useEffect(() => {
    if (!selectedId) return;
    const element = Array.from(document.querySelectorAll<HTMLElement>("[data-item-id]")).find(
      (candidate) => candidate.dataset.itemId === selectedId
    );
    if (element && typeof element.scrollIntoView === "function") {
      element.scrollIntoView({ block: "nearest" });
    }
  }, [selectedId, view, pinnedRow?.itemId]);

  // Command palette (E280, PI-SPEC-SW-1399): "Backlog: switch to Board/List" while the backlog is open.
  const paletteCommands = useMemo(
    (): GroomingCommand[] => [
      {
        id: "backlog-switch-view",
        label: view === "list" ? "Backlog: switch to Board" : "Backlog: switch to List",
        icon: view === "list" ? Kanban : List,
        hint: "B",
        keywords: "stages board list view",
        run: toggleView,
      },
    ],
    [view, toggleView]
  );
  useRegisterCommands("backlog", paletteCommands);

  const total = backlog.data?.totalBacklogItems;
  const allLabel =
    total === undefined ? "All" : `All ${String(total)}${backlog.data?.truncated ? "+" : ""}`;
  // No pane until an item is picked (UX-PM-30): the list then has the whole width.
  const showPane = Boolean(selectedId) && !(view === "list" && paneHidden);
  const expanded = showPane && paneExpanded;
  const shownPaneWidth = clampPaneWidth(paneWidth ?? defaultPaneWidth(splitWidth), splitWidth);

  return (
    <div className="flex flex-col gap-3" data-testid="grooming-backlog">
      <div className="flex items-center gap-3">
        {/* The title keeps room for itself; the controls wrap, right-aligned, instead of clipping it. */}
        <div className="flex min-w-[180px] flex-1 flex-col gap-0.5">
          <h1 className={TOOLBOX_PAGE_TITLE}>Backlog</h1>
          <span
            className="truncate text-[13px] text-muted-foreground"
            data-testid="backlog-subtitle"
          >
            {backlogSubtitle(sort, backlog.data, visible.length, searching || stageFilter !== null)}
          </span>
        </div>
        <div className="flex min-w-0 flex-wrap items-center justify-end gap-3">
          <FindBox value={find} onChange={setFind} inputRef={findRef} disabled={unreadable} />
          <Segmented<GroomingBacklogSort>
            label="Sort"
            value={sort}
            onChange={setSort}
            disabled={unreadable}
            options={BACKLOG_SORT_MENU.map((value) => ({ value, label: BACKLOG_SORT_LABELS[value] }))}
          />
          <Segmented<"top" | "all">
            label="How many items"
            value={top === "all" ? "all" : "top"}
            disabled={unreadable}
            onChange={(value) => {
              setTop(value === "all" ? "all" : BACKLOG_TOP_DEFAULT);
            }}
            options={[
              { value: "top", label: `Top ${String(BACKLOG_TOP_DEFAULT)}` },
              { value: "all", label: allLabel },
            ]}
          />
          <Segmented<View>
            label="View"
            value={view}
            onChange={setView}
            disabled={unreadable}
            options={[
              { value: "list", label: "List" },
              { value: "stages", label: "Stages" },
            ]}
          />
          {progressAllowed && <ProgressPanelButton open={showProgress} onClick={toggleProgress} />}
          <KeyboardKeysButton canAddToSession={agenda.canEdit} />
          {view === "list" && paneHidden && selectedId && (
            <button
              type="button"
              className="inline-flex h-[30px] items-center gap-1.5 rounded-lg px-2.5 text-sm font-medium text-muted-foreground hover:bg-[#141417] hover:text-foreground"
              title="Show the reading pane"
              onClick={() => {
                setPaneHidden(false);
              }}
            >
              <PanelRightOpen className="h-3.5 w-3.5" aria-hidden="true" />
              Show details
            </button>
          )}
        </div>
      </div>

      {unreadable ? (
        <AdoReadErrorCard query={backlog} subject="backlog" project={project} testId="backlog-error" />
      ) : backlog.isLoading ? (
        <div className="flex min-h-[240px] items-center justify-center" role="status" aria-live="polite">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          <span className="sr-only">Reading the backlog from ADO…</span>
        </div>
      ) : backlog.data && progressItems ? (
        <GroomingProgressItems
          project={project}
          kind={progressItems}
          sinceDays={progressWindow}
          onSinceDaysChange={setProgressWindow}
          backlog={backlog.data}
          onBack={backToProgress}
          onOpenItem={openProgressItem}
          summaryFor={summaryFor}
          enabled={!backlog.isFetching}
        />
      ) : backlog.data ? (
        <>
          <Tiles backlog={backlog.data} sort={sort} filter={stageFilter} onFilter={setStageFilter} />
          {stageFilter && (
            <FilterChipRow
              stage={stageFilter}
              count={visible.length}
              onClear={() => {
                setStageFilter(null);
              }}
            />
          )}
          <Exceptions backlog={backlog.data} onSelect={select} />
          {searching && (
            <p className="text-[13px] text-muted-foreground" role="status" data-testid="find-count">
              {whole.isLoading
                ? "Searching the whole backlog…"
                : whole.isError
                  ? "The whole backlog could not be read. Showing matches from the items loaded."
                  : `${String(visible.length)} ${visible.length === 1 ? "item matches" : "items match"} in the whole backlog`}
            </p>
          )}
          <div
            ref={setSplitElement}
            className="glass-card relative flex min-h-[520px] items-stretch rounded-[10px]"
            data-testid="backlog-split"
          >
            {/* Full width hides the list without unmounting it, so its place and the backlog read stay. */}
            <div
              className={cn("flex min-w-0 flex-1", expanded && "hidden")}
              data-testid="backlog-list-area"
            >
              {visible.length === 0 && !pinnedRow ? (
                <p
                  className="min-w-0 flex-1 p-5 text-sm text-muted-foreground"
                  data-testid="backlog-empty"
                >
                  {searching
                    ? `No items match "${find.trim()}".`
                    : stageFilter
                      ? `No items in ${STAGE_LABELS[stageFilter]} among the items loaded.`
                      : "The backlog is empty."}
                </p>
              ) : view === "list" ? (
                <ListView
                  project={project}
                  items={visible}
                  positions={positions}
                  selectedId={selectedId}
                  agenda={agenda}
                  onSelect={select}
                  layout={
                    showPane
                      ? "compact"
                      : showProgress
                        ? "progress"
                        : stageFilter === "needs-information"
                          ? "author"
                          : "full"
                  }
                  nextStep={nextStep}
                  pinned={pinnedRow}
                />
              ) : (
                <StagesView
                  project={project}
                  items={visible}
                  positions={positions}
                  selectedId={selectedId}
                  agenda={agenda}
                  onSelect={select}
                  pinned={pinnedRow}
                />
              )}
            </div>
            {showPane && selectedId && (
              <ReadingPane
                project={project}
                itemId={selectedId}
                // E292 (UX-PM-12): "#N in the backlog" is the ADO position, in every sort, filter and find.
                position={positions.get(selectedId) ?? null}
                variant={view === "list" ? "side" : "over"}
                agenda={agenda}
                preparing={selectedRow?.preparing ?? null}
                onClose={close}
                width={shownPaneWidth}
                maxWidth={
                  splitWidth > 0 ? Math.max(PANE_WIDTH_MIN, splitWidth - LIST_MIN_WIDTH) : undefined
                }
                onResize={resizePane}
                expanded={expanded}
                onToggleExpand={togglePaneExpanded}
              />
            )}
            {/* The metrics read (inside the panel) waits while the backlog read is in flight and is re-read
                after it (useMetricsAfterBacklogRead), so it counts the team points that read just recorded. */}
            {showProgress && (
              <GroomingProgressPanel
                project={project}
                sinceDays={progressWindow}
                onSinceDaysChange={setProgressWindow}
                enabled={!backlog.isFetching}
                sprintEntries={backlog.data.exceptions.length}
                variant={view === "list" ? "side" : "over"}
                onClose={closeProgress}
                summaryFor={summaryFor}
                onOpenItems={openProgressItems}
              />
            )}
          </div>
        </>
      ) : null}
    </div>
  );
}
